import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';

/** Where the X of several report settings pages goes (Angular `SETTINGS_ROUTE`). */
export const SETTINGS_ROUTE = '/home/taxreturns/tasks-accountingperiods';

/**
 * The client guard every page of this group runs in `ngOnInit`: no selected client -> error toast and back to Accounts.
 * The client is read once, like Angular did.
 */
export function useReportClient(onMissing?: () => void): SelectedClient | null {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  useEffect(() => {
    if (client) return;
    notifyError('Select a client from Accounts first.');
    onMissing?.();
    navigate('/home/accountclient');
    // Runs once on mount, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return client;
}

/** Angular `FormValidationService.validateForm`: true when valid, otherwise the mandatory-fields toast. */
export function validateForm(isValid: boolean): boolean {
  if (isValid) return true;
  notifyError(NotificationTextMessage.validationMessage);
  return false;
}

interface FrameProps {
  title: string;
  /** Extra class on `.sidenav-details`: the root class a ported component `.scss` is wrapped in. */
  hostClassName?: string;
  /** The X button; omit for the pages whose X has no click handler in Angular. */
  onClose?: () => void;
  children: ReactNode;
}

/**
 * The frame every Report Settings page shares: the settings menu, then `sidenav-details` with the title row.
 *
 * 4 Nov design review: the page is already one tab of the Report Settings bar above it, which names it - so the
 * title is no longer shown a second time, just the info icon and the close button. It stays in the DOM
 * (`cdk-visually-hidden`) as the page's accessible name.
 */
export function ReportSettingsFrame({ title, hostClassName = '', onClose, children }: FrameProps) {
  return (
    <SideMenuPage menu="settings">
      <div className={`sidenav-details ${hostClassName}`.trim()}>
        {/* sidenav-header--compact: the title is visually hidden (the Report Settings bar above already names the
            page), so this row is just the info icon and close button - the full title-row padding would leave it
            looking like an oversized empty bar. */}
        <div className="sidenav-header sidenav-header--compact">
          <div className="d-flex align-items-center justify-space-between">
            {/* 2 Nov design review: the "Changes here apply..." banner is one small info icon beside the title. */}
            <div className="d-flex align-items-center gap-10">
              <h2 className="cdk-visually-hidden">{title}</h2>
              <InfoHint text="Changes here apply to new reports and to any report you regenerate. Existing reports keep the settings they were made with." />
            </div>
            <Button className="close-btn" onClick={onClose}>
              <Icon name="close" />
            </Button>
          </div>
        </div>
        {children}
      </div>
    </SideMenuPage>
  );
}

/** Angular's `{{ number | number: '1.2-4' }}`; null/undefined show nothing. */
export function formatDecimal(value: number | null | undefined, minFraction: number, maxFraction: number): string {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
  }).format(value);
}
